
import { defineComponent } from "vue";
import { mapGetters } from "vuex";

import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import timezone from "dayjs/plugin/timezone";
import { RankStatus } from "@/store/Auth/AuthInterfaces";
import { Rank } from "@/store/Ranks/RanksInterfaces";
import {
  RankViewer,
  heldRankIdOf,
  visibleNextRank,
  visibleRanks,
} from "@shared/entities/Ranks/RankVisibility";
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

export default defineComponent({
  name: "ranks-card",
  props: {
    displayedRank: {
      type: String,
      default: "current",
    },
    cardTitle: {
      type: String,
      default: "Plan de Carrera",
    },
    userRanksCarrerData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      ranksData: new Map<string, Rank>(),
      displayedRankId: "INITIAL",
      // Dirección del deslizamiento del <transition>: 'slide-next' (nuevo entra
      // por la derecha) o 'slide-prev' (por la izquierda). La fija slideNext/
      // slidePrev ANTES de mover displayedRankId para que las clases de la
      // transición apunten al lado correcto.
      slideDir: "slide-next",
    };
  },
  methods: {
    isDisplayedRank(rankID: string) {
      return rankID == this.displayedRankId;
    },
    // El carrusel lo maneja Vue por completo (displayedRankId = ítem activo).
    // Antes lo conducía Bootstrap vía data-bs-ride/data-bs-slide, pero la clase
    // `.active` reactiva de Vue y el estado interno de Bootstrap se peleaban:
    // Bootstrap arrancaba con el ítem inicial y, al mover Vue el `.active`,
    // desincronizaba y deslizaba al ÚLTIMO rango (UNKNOWN, prio 999999). Sin
    // data-api de Bootstrap, prev/next solo mueven displayedRankId por el orden
    // de ranksData y el CSS muestra el activo — determinista, sin saltos.
    slidePrev() {
      const keys = Array.from(this.ranksData.keys());
      const i = keys.indexOf(this.displayedRankId);
      if (i > 0) {
        this.slideDir = "slide-prev";
        this.displayedRankId = keys[i - 1];
      }
    },
    slideNext() {
      const keys = Array.from(this.ranksData.keys());
      const i = keys.indexOf(this.displayedRankId);
      if (i > -1 && i < keys.length - 1) {
        this.slideDir = "slide-next";
        this.displayedRankId = keys[i + 1];
      }
    },
    // Color de tier del rango (token --rank-<id>); gris de respaldo si el
    // rango no tiene token (p.ej. datos legacy del emulador).
    rankColorVar(rankID: string): string {
      return `var(--rank-${rankID}, var(--gtr-mid, #5a5a5a))`;
    },
    toTwoDecimals(num: number): string {
      return num.toLocaleString(undefined, {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      });
    },
    formatDateTimezone(milliseconds: number) {
      return dayjs(milliseconds).format("YYYY/MM/DD HH:mm:ss z");
    },
    isRank(rankID: string, status: string): boolean {
      if (this.ranksCarrerData instanceof Map) {
        const r: RankStatus = this.ranksCarrerData.get(rankID) as RankStatus;
        return (r || false) && r.status == status;
      } else {
        return false;
      }
    },
    isCurrentRank(rankID: string) {
      return this.isRank(rankID, "current");
    },
    isNextRank(rankID: string) {
      const r = this.isRank(rankID, "next");
      return r;
    },
    isRankLocked(rankID: string) {
      return this.isRank(rankID, "locked");
    },
    isRankUnlocked(rankID: string) {
      return this.isRank(rankID, "unlocked");
    },
    rankDate(rankID: string) {
      if (this.ranksCarrerData instanceof Map) {
        const _rankCarrerData: RankStatus = this.ranksCarrerData.get(
          rankID
        ) as RankStatus;
        return _rankCarrerData?.date;
      }
      return undefined;
    },
    setRanks() {
      // El carrusel muestra la escalera de carrera de ESTA persona (la del prop
      // `userRanksCarrerData`: en Home es la del usuario, en Unilevel la del
      // líder consultado). Se le quitan (1) los centinelas —hoy solo UNKNOWN,
      // "Unkown Secret", que no se otorga jamás y solo existe para que "el rango
      // de encima del último" resuelva a algo— y (2) los rangos restringidos que
      // esta persona NO tiene: COFUNDADOR solo lo ve un cofundador, así que para
      // los demás la escalera se corta en BLACK-CROWN. La regla, los tres
      // niveles y el porqué de que uno viva en código y el otro en la bandera
      // `hiddenInProgress`, en @shared/entities/Ranks/RankVisibility.
      //
      // Filtrar aquí también blinda el «próximo rango» de esta tarjeta: el
      // computed `ranksCarrerData` recorre ESTE mapa, así que ni un centinela ni
      // un rango ajeno pueden colarse como status "next".
      this.ranksData = visibleRanks(
        this.allRanks() as Map<string, Rank> | undefined,
        this.rankViewer
      );
    },
    clearRanks() {
      this.ranksData = new Map<string, Rank>();
    },
  },
  computed: {
    ...mapGetters("ranks", ["rankById", "allRanks"]),
    // Quién mira ESTA tarjeta: el dueño de la escalera que se está pintando. No
    // se puede preguntar al store de sesión porque la tarjeta también pinta la
    // escalera de otro (vista Unilevel), así que el rango que tiene sale del
    // propio prop —la entrada con status "current"—, y la bandera
    // `hiddenInProgress` sale del catálogo vía `rankById`.
    rankViewer(): RankViewer {
      return {
        heldRankId: heldRankIdOf(this.userRanksCarrerData),
        rankById: this.rankById,
      };
    },
    // El único rango que se pinta: el que corresponde a displayedRankId. Es la
    // fuente del slide activo del <transition> (antes lo hacía el v-for + la
    // clase .active de Bootstrap sobre TODOS los ítems).
    activeRank(): Rank | undefined {
      return this.ranksData.get(this.displayedRankId);
    },
    atStart(): boolean {
      return (
        Array.from(this.ranksData.keys()).indexOf(this.displayedRankId) <= 0
      );
    },
    atEnd(): boolean {
      const keys = Array.from(this.ranksData.keys());
      const i = keys.indexOf(this.displayedRankId);
      return i === -1 || i >= keys.length - 1;
    },
    // displayedRankId() {

    // },
    ranksCarrerData() {
      // this.clearRanks();
      // this.setRanks();
      const ranks = this.ranksData; //this.allRanks() as Map<string, Rank>;
      const ranksCarrerData = new Map<string, RankStatus>();
      const carrerData = this.userRanksCarrerData;
      let previousStatus = "";
      ranks.forEach((r, k) => {
        let c = carrerData[k];
        if (c == undefined) {
          c = {
            _id: k,
            progress: 0,
            status: previousStatus == "current" ? "next" : "locked",
            priority: 0,
          };
        }
        const rankStatus: RankStatus = {
          _id: k,
          priority: r.priority,
          progress: c.progress,
          status: c.status,
          date: c.date ? c.date?.toDate() : undefined,
        };
        ranksCarrerData.set(k, rankStatus);
        previousStatus = rankStatus.status;
      });
      return ranksCarrerData;
    },
    currentRank(): RankStatus | undefined {
      if (this.ranksCarrerData instanceof Map) {
        for (const rank of this.ranksCarrerData.values()) {
          if (rank.status === "current") {
            return rank;
          }
        }
      }
      return undefined;
    },
    nextRank(): RankStatus | undefined {
      if (this.ranksCarrerData instanceof Map) {
        for (const rank of this.ranksCarrerData.values()) {
          if (rank.status === "next") {
            // Cinturón y tirantes: `ranksCarrerData` ya viene del mapa filtrado,
            // pero la meta que se le PINTA al usuario nunca puede ser ni un
            // centinela ni un rango restringido que no tenga. Un BLACK-CROWN
            // termina en BLACK-CROWN; un cofundador está en la cima y tampoco
            // tiene "siguiente".
            return visibleNextRank(rank, this.rankViewer);
          }
        }
      }
      return undefined;
    },
  },
  mounted() {
    this.setRanks();
    let rankObj;
    switch (this.displayedRank.toLowerCase()) {
      case "next":
        rankObj = this.nextRank;
        break;
      case "current":
        rankObj = this.currentRank;
        break;
      default:
        rankObj = this.rankById(this.displayedRank);
    }
    rankObj = rankObj != undefined ? rankObj : this.currentRank;
    this.displayedRankId = rankObj ? rankObj._id : "INITIAL";
  },
});
